import React, { useState, useEffect } from 'react';
import { Card, Table, Typography, Row, Col, Button, Spin, message, Space, Tooltip, Modal, Form, InputNumber, Input, Select, Popconfirm, Statistic } from 'antd';
import { EditOutlined, DeleteOutlined, InfoCircleOutlined, FileTextOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import CustomDatePicker from '../components/CustomDatePicker';
import { listUsers } from '../services/userService';
import { getMonthlyAdvanceDetails, addAdvanceLog, updateAdvanceLog, deleteAdvanceLog } from '../services/advanceService';
import { getSalarySlip } from '../services/salarySlipService';
import { themeConfig } from '../config/theme';
import dayjs from 'dayjs';
import { renderActivityDot } from '../utils/activityDot';

const { Option } = Select;
const { Title, Text } = Typography;

const AdvanceManagement = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(dayjs().startOf('month'));
  
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [advanceDetails, setAdvanceDetails] = useState(null);
  const [isSlipGenerated, setIsSlipGenerated] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [previousMonthSlip, setPreviousMonthSlip] = useState(null);
  const [pdfModalVisible, setPdfModalVisible] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await listUsers();
        setUsers(response.users || []);
      } catch (error) {
        message.error('Failed to fetch users');
      }
    };
    fetchUsers();
  }, []);

  const handleLoadData = async (monthToLoad = null) => {
    // Ensure we always have a valid dayjs object
    console.log(monthToLoad);
    let monthToUse;
    if (monthToLoad) {
      // monthToLoad should already be a dayjs object from navigation functions
      monthToUse = dayjs(monthToLoad);
    } else {
      monthToUse = selectedMonth;
    }
    
    console.log('Debug - selectedUser:', selectedUser);
    console.log('Debug - monthToLoad:', monthToLoad);
    console.log('Debug - selectedMonth:', selectedMonth);
    console.log('Debug - monthToUse:', monthToUse);
    console.log('Debug - monthToUse type:', typeof monthToUse);
    console.log('Debug - monthToUse.isValid():', monthToUse?.isValid?.());
    
    if (!selectedUser) {
      messageApi.warning('Please select a user.');
      return;
    }
    
    if (!monthToUse || !monthToUse.isValid || !monthToUse.isValid()) {
      messageApi.warning('Please select a valid month.');
      return;
    }
    setLoading(true);
    setAdvanceDetails(null);
    setIsSlipGenerated(false);
    setPreviousMonthSlip(null);
    try {
      const year = monthToUse.year();
      const month = monthToUse.month() + 1;

      // Check if slip is generated for the month to disable actions
      try {
          await getSalarySlip(selectedUser, year, month);
          setIsSlipGenerated(true);
      } catch (error) {
          if (error.response && error.response.status !== 404) {
              message.error('Could not verify salary slip status.');
          }
          setIsSlipGenerated(false);
      }

      // Check previous month salary slip
      const prevMonth = monthToUse.subtract(1, 'month');
      const prevYear = prevMonth.year();
      const prevMonthNum = prevMonth.month() + 1;
      
      try {
        const prevSlip = await getSalarySlip(selectedUser, prevYear, prevMonthNum);
        console.log(prevSlip);
        setPreviousMonthSlip(prevSlip.salarySlip);
      } catch (error) {
        if (error.response && error.response.status !== 404) {
          console.error('Error fetching previous month slip:', error);
        }
        setPreviousMonthSlip(null);
      }

      const data = await getMonthlyAdvanceDetails(selectedUser, year, month);
      setAdvanceDetails(data);
    } catch (error) {
      message.error('Failed to load advance data.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddAdvance = async (values) => {
    setSubmitting(true);
    try {
        await addAdvanceLog(
            selectedUser,
            values.amount,
            values.date.toISOString(),
            values.notes
        );
        message.success("Advance added successfully!");
        form.resetFields(['amount', 'notes']);
        handleLoadData(); // Refresh data
    } catch(error) {
        messageApi.error(error.data?.message || 'Failed to add advance.');
        console.error(error);
    } finally {
        setSubmitting(false);
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    editForm.setFieldsValue({
        amount: record.Amount,
        notes: record.Notes,
    });
    setIsEditModalVisible(true);
  };

  const handleUpdate = async (values) => {
    setSubmitting(true);
    try {
        await updateAdvanceLog(
            selectedUser,
            editingRecord.Timestamp,
            values.amount,
            values.notes
        );
        message.success("Advance updated successfully!");
        setIsEditModalVisible(false);
        setEditingRecord(null);
        handleLoadData(); // Refresh
    } catch (error) {
        messageApi.error(error.response?.data?.message || 'Failed to update advance.');
    } finally {
        setSubmitting(false);
    }
  };

  const handleDelete = async (record) => {
    try {
        await deleteAdvanceLog(selectedUser, record.Timestamp);
        message.success("Advance deleted successfully!");
        handleLoadData(); // Refresh
    } catch (error) {
        messageApi.error(error.response?.data?.message || 'Failed to delete advance.');
    }
  };

  const handleViewPdf = async () => {
    if (!previousMonthSlip) return;
    
    setPdfUrl(previousMonthSlip.pdfUrl);
    setPdfModalVisible(true);
  };

  const handlePreviousMonth = () => {
    const newMonth = selectedMonth.subtract(1, 'month');
    console.log('Previous month - selectedMonth:', selectedMonth);
    console.log('Previous month - newMonth:', newMonth);
    console.log('Previous month - newMonth.isValid():', newMonth.isValid());
    setSelectedMonth(newMonth);
    // Auto-load data if user is selected
    if (selectedUser) {
      handleLoadData(newMonth);
    }
  };

  const handleNextMonth = () => {
    const newMonth = selectedMonth.add(1, 'month');
    console.log('Next month - selectedMonth:', selectedMonth);
    console.log('Next month - newMonth:', newMonth);
    console.log('Next month - newMonth.isValid():', newMonth.isValid());
    setSelectedMonth(newMonth);
    // Auto-load data if user is selected
    if (selectedUser) {
      handleLoadData(newMonth);
    }
  };

  const handleMonthChange = (value) => {
    const [year, month] = value.split('-');
    setSelectedMonth(dayjs().year(parseInt(year)).month(parseInt(month) - 1).startOf('month'));
  };

  // Generate month options for the last 2 years and current year
  const generateMonthOptions = () => {
    const options = [];
    const currentYear = dayjs().year();
    
    // Add last 2 years
    for (let year = currentYear - 2; year <= currentYear; year++) {
      for (let month = 0; month < 12; month++) {
        const date = dayjs().year(year).month(month).startOf('month');
        if (date.isSameOrBefore(dayjs().endOf('month'))) {
          options.push({
            value: `${year}-${month + 1}`,
            label: date.format('MMMM YYYY')
          });
        }
      }
    }
    return options.reverse(); // Most recent first
  };

  const columns = [
    {
        title: 'Date',
        dataIndex: 'Timestamp',
        key: 'date',
        render: (text, record) => record.isCarryForward ? 
            <Text strong>{record.Timestamp}</Text> : 
            dayjs(text).format('ddd, DD MMM YYYY'),
    },
    {
        title: 'Amount',
        dataIndex: 'Amount',
        key: 'amount',
        render: (amount) => `₹${amount.toFixed(2)}`
    },
    {
        title: 'Notes',
        dataIndex: 'Notes',
        key: 'notes',
        render: (text, record) => {
            if (record.isCarryForward) {
                if (record.carryForwardSource === 'SALARY_SLIP') return 'From previous month\'s salary slip';
                if (record.carryForwardSource === 'SUM_OF_PREVIOUS_MONTH_ADVANCES') return 'Sum of all advances in previous month';
                return '';
            }
            return text;
        }
    },
    {
        title: 'Actions',
        key: 'actions',
        render: (_, record) => {
            if (record.isCarryForward) return null;
            return (
                <Space size="middle">
                    <Tooltip title="Edit">
                        <Button
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                            disabled={isSlipGenerated}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Delete this advance?"
                        description="This action cannot be undone."
                        onConfirm={() => handleDelete(record)}
                        okText="Yes"
                        cancelText="No"
                        disabled={isSlipGenerated}
                    >
                        <Tooltip title={isSlipGenerated ? "Cannot delete, salary slip is generated" : "Delete"}>
                            <Button icon={<DeleteOutlined />} danger disabled={isSlipGenerated} />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            );
        }
    }
  ];

  const tableData = advanceDetails ? [
    {
        key: 'carry-forward',
        Timestamp: 'Carry Forward',
        Amount: advanceDetails.carryForward,
        isCarryForward: true,
        carryForwardSource: advanceDetails.carryForwardSource,
    },
    ...advanceDetails.advancesForMonth.map(adv => ({...adv, key: adv.SK}))
  ] : [];

  const totalForMonth = advanceDetails ? advanceDetails.advancesForMonth.reduce((sum, item) => sum + item.Amount, 0) : 0;
  const totalWithCarryForward = totalForMonth + (advanceDetails?.carryForward || 0);

  return (
    <div>
        {contextHolder}
        <Title level={3}>Advance Management</Title>
        <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col>
                <Select
                    showSearch
                    placeholder="Select a user"
                    style={{ width: 300 }}
                    value={selectedUser}
                    onChange={setSelectedUser}
                    optionFilterProp="label"
                    filterOption={(input, option) => {
                        const searchText = input.toLowerCase();
                        const optionText = option.label.toLowerCase();
                        return optionText.includes(searchText);
                    }}
                    notFoundContent={
                        users.length === 0 ? 
                        "No users available" : 
                        null
                    }
                >
                    {users.map((user) => {
                      const userLabel = `${user.FullName} (${user.Job}) - ${user.MobileNumber}`;
                      return (
                        <Option key={user.MobileNumber} value={user.MobileNumber} label={userLabel}>
                          <span>
                            {renderActivityDot(user.activeStatus)} {userLabel}
                          </span>
                        </Option>
                      );
                    })}
                </Select>
            </Col>
            <Col>
                <Space>
                    <Button 
                        icon={<LeftOutlined />} 
                        onClick={handlePreviousMonth}
                        disabled={loading}
                    >
                        Previous
                    </Button>
                    <Select
                        placeholder="Select Month"
                        style={{ width: 200 }}
                        value={`${selectedMonth.year()}-${selectedMonth.month() + 1}`}
                        onChange={handleMonthChange}
                        options={generateMonthOptions()}
                    />
                    <Button 
                        icon={<RightOutlined />} 
                        onClick={handleNextMonth}
                        disabled={loading}
                    >
                        Next
                    </Button>
                </Space>
            </Col>
            <Col>
                <Button type="primary" onClick={() => handleLoadData()} loading={loading}>Load Advance Data</Button>
            </Col>
        </Row>
        
        <Spin spinning={loading}>
            {previousMonthSlip ? (
                <Card 
                    title={
                        <Space>
                            <FileTextOutlined />
                            Previous Month Salary Slip Status
                            <Tooltip title="View PDF">
                                <Button 
                                    type="text" 
                                    icon={<InfoCircleOutlined />} 
                                    onClick={handleViewPdf}
                                    size="small"
                                />
                            </Tooltip>
                        </Space>
                    }
                    style={{ marginBottom: 16 }}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Statistic 
                                title="Salary Pending" 
                                value={(previousMonthSlip.NetSalaryPaid || 0) - (previousMonthSlip.partialPaidAmount || 0)} 
                                prefix="₹" 
                                precision={2}
                            />
                        </Col>
                        <Col span={12}>
                            <Statistic 
                                title="Advance Deducted" 
                                value={previousMonthSlip.AdvanceDeductedThisMonth} 
                                prefix="₹" 
                                precision={2}
                            />
                        </Col>
                        <Col span={12}>
                            <Statistic 
                                title="Salary Paid" 
                                value={previousMonthSlip.partialPaidAmount} 
                                prefix="₹" 
                                precision={2}
                            />
                        </Col>
                        <Col span={12}>
                            <Statistic 
                                title="Advance Carried Forward" 
                                value={previousMonthSlip.AdvanceCarriedForward} 
                                prefix="₹" 
                                precision={2}
                            />
                        </Col>
                    </Row>
                </Card>
            ) : advanceDetails && (
                <Card 
                    title={
                        <Space>
                            <FileTextOutlined />
                            Previous Month Salary Slip Status
                        </Space>
                    }
                    style={{ marginBottom: 16 }}
                >
                    <div style={{ textAlign: 'center', padding: '20px', color: themeConfig.text.muted }}>
                        <FileTextOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
                        <div>Previous month salary slip is not yet generated</div>
                    </div>
                </Card>
            )}
            {advanceDetails && (
                <Row gutter={24}>
                    <Col xs={24} md={14}>
                        <Table
                            columns={columns}
                            dataSource={tableData}
                            pagination={false}
                            bordered
                            size="small"
                            summary={() => (
                                <Table.Summary.Row>
                                    <Table.Summary.Cell index={0}><Text strong>Total this month (with C/F)</Text></Table.Summary.Cell>
                                    <Table.Summary.Cell index={1}><Text strong>₹{totalWithCarryForward.toFixed(2)}</Text></Table.Summary.Cell>
                                    <Table.Summary.Cell index={2}></Table.Summary.Cell>
                                </Table.Summary.Row>
                            )}
                        />
                    </Col>
                    <Col xs={24} md={10}>
                        <Card title="Add New Advance">
                            <Form form={form} layout="vertical" onFinish={handleAddAdvance} initialValues={{ date: dayjs() }}>
                                <Form.Item
                                    name="amount"
                                    label="Advance Amount"
                                    rules={[{ required: true, message: 'Please enter an amount' }]}
                                >
                                    <InputNumber prefix="₹" style={{width: '100%'}} min={1}/>
                                </Form.Item>
                                <Form.Item
                                    name="date"
                                    label="Date of Advance"
                                    rules={[{ required: true, message: 'Please select a date' }]}
                                >
                                    <CustomDatePicker 
                                      style={{width: '100%'}} 
                                      defaultValue={dayjs()}
                                      showToday={true}
                                      disabledDate={(current) => current && current > dayjs().endOf('day')}
                                      placeholder="Select Date of Advance"
                                    />
                                </Form.Item>
                                <Form.Item
                                    name="notes"
                                    label="Notes"
                                >
                                    <Input.TextArea rows={2} />
                                </Form.Item>
                                <Form.Item>
                                    <Button type="primary" htmlType="submit" loading={submitting} block>
                                        Submit Advance
                                    </Button>
                                </Form.Item>
                            </Form>
                        </Card>
                    </Col>
                </Row>
            )}
        </Spin>
        <Modal
            title="Edit Advance Log"
            open={isEditModalVisible}
            onCancel={() => setIsEditModalVisible(false)}
            footer={null}
            destroyOnClose
        >
            <Spin spinning={submitting}>
                <Form form={editForm} layout="vertical" onFinish={handleUpdate}>
                    <Form.Item
                        name="amount"
                        label="Advance Amount"
                        rules={[{ required: true }]}
                    >
                        <InputNumber prefix="₹" style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="notes" label="Notes">
                        <Input.TextArea rows={2} />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit" block>
                            Update Advance
                        </Button>
                    </Form.Item>
                </Form>
            </Spin>
        </Modal>
        
        <Modal
            title="Salary Slip PDF"
            open={pdfModalVisible}
            onCancel={() => setPdfModalVisible(false)}
            footer={[
                <Button key="close" onClick={() => setPdfModalVisible(false)}>
                    Close
                </Button>
            ]}
            width="90%"
            style={{ top: 20 }}
        >
            {pdfUrl && (
                <iframe
                    src={pdfUrl}
                    width="100%"
                    height="600px"
                    style={{ border: 'none' }}
                    title="Salary Slip PDF"
                />
            )}
        </Modal>
    </div>
  );
};

export default AdvanceManagement; 